import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  createPaqueteSchema,
  updatePaqueteSchema,
  type CreatePaqueteInput,
  type UpdatePaqueteInput,
} from "@/lib/validaciones/paquete.validacion";
import {
  useRegisterPaquete,
  useUpdatePaquete,
} from "@/hooks/paquetes/usePaquetes";
import type { Paquete } from "@/interfaces/paquetes.interface";
import { SelectPrograma } from "@/components/ui/SelectPrograma";

interface DetallePaqueteProps {
  data: Paquete;
}

export function RegisterPaqueteForm({
  idPrograma,
  setOpen,
}: {
  idPrograma?: number;
  setOpen: (v: boolean) => void;
}) {
  // Calcula vencimiento como +1 año a partir de la fecha de inicio.
  // Se ejecuta una vez por montaje del modal.
  const { fechaInicio, fechaVencimiento } = useMemo(() => {
    const inicio = new Date();
    const venc = new Date(inicio);
    venc.setFullYear(venc.getFullYear() + 1);
    return { fechaInicio: inicio, fechaVencimiento: venc };
  }, []);

  const form = useForm<CreatePaqueteInput>({
    resolver: zodResolver(createPaqueteSchema),
    defaultValues: {
      idPrograma: idPrograma ?? 0,
      cantidadLearners: 1,
      fechaInicio,
      fechaVencimiento,
    },
  });

  const { mutate, isPending } = useRegisterPaquete();

  const onSubmit = (data: CreatePaqueteInput) => {
    mutate(data, {
      onSuccess: () => {
        setOpen(false);
        form.reset();
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {typeof idPrograma === "undefined" && (
            <FormField
              control={form.control}
              name="idPrograma"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Programa</FormLabel>
                  <FormControl>
                    <SelectPrograma
                      value={field.value ? String(field.value) : null}
                      onChange={(option) =>
                        field.onChange(option ? Number(option.value) : 0)
                      }
                      placeholder="Selecciona un programa..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            name="cantidadLearners"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Aprendices</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {["fechaInicio", "fechaVencimiento"].map((name) => (
            <FormField
              key={name}
              name={name as "fechaInicio" | "fechaVencimiento"}
              control={form.control}
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormLabel required>
                    {name === "fechaInicio" ? "Inicio" : "Vencimiento"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      value={field.value?.toISOString().split("T")[0] ?? ""}
                      onChange={(e) => field.onChange(new Date(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending ? "Guardando..." : "Crear Paquete"}
        </Button>
      </form>
    </Form>
  );
}

export function EditPaqueteForm({
  data,
  close,
}: {
  data: UpdatePaqueteInput;
  close: () => void;
}) {
  const form = useForm<UpdatePaqueteInput>({
    resolver: zodResolver(updatePaqueteSchema),
    defaultValues: data,
  });

  const { mutate, isPending } = useUpdatePaquete();

  const onSubmit = (data: UpdatePaqueteInput) => {
    mutate(data, {
      onSuccess: () => close(),
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="idPrograma"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Programa</FormLabel>
                <FormControl>
                  <SelectPrograma
                    value={field.value ? String(field.value) : null}
                    onChange={(option) =>
                      field.onChange(option ? Number(option.value) : 0)
                    }
                    placeholder="Selecciona un programa..."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="cantidadLearners"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Aprendices</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {["fechaInicio", "fechaVencimiento" as const].map((name) => (
            <FormField
              key={name}
              name={name as "fechaInicio" | "fechaVencimiento"}
              control={form.control}
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormLabel required>
                    {name === "fechaInicio" ? "Inicio" : "Vencimiento"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      value={
                        field.value
                          ? field.value instanceof Date
                            ? field.value.toISOString().split("T")[0]
                            : new Date(field.value as unknown as string)
                                .toISOString()
                                .split("T")[0]
                          : ""
                      }
                      onChange={(e) => field.onChange(new Date(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </form>
    </Form>
  );
}

export function DetallePaquete({ data }: DetallePaqueteProps) {
  return (
    <div className="space-y-4 text-sm">
      <div>
        <strong>ID:</strong> {data.id}
      </div>
      <div>
        <strong>Programa:</strong> {data.programa?.nombre ?? data.idPrograma}
      </div>
      <div>
        <strong>Cantidad de aprendices:</strong> {data.cantidadLearners}
      </div>
      <div>
        <strong>Fecha de inicio:</strong>{" "}
        {new Date(data.fechaInicio).toLocaleDateString()}
      </div>
      <div>
        <strong>Fecha de vencimiento:</strong>{" "}
        {new Date(data.fechaVencimiento).toLocaleDateString()}
      </div>
      <div>
        <strong>Creado por:</strong> {data.creadoPor}
      </div>
      <div>
        <strong>Actualizado por:</strong> {data.actualizadoPor}
      </div>
      <div>
        <strong>Activo:</strong> {data.isActivo ? "Sí" : "No"}
      </div>
      <div>
        <strong>Oculto:</strong> {data.isOculto ? "Sí" : "No"}
      </div>
    </div>
  );
}
